type TimerSession = {
    start: number;
    end?: number;
};

/**
 * A simple performance tracker for timing various operations in the app. 
 * It allows starting, stopping, and peeking timers by name, as well as exporting all timing data as JSON 
 * for debugging and performance analysis.
 */
class Tracker {

    private sessions: Map<string, TimerSession> = new Map();

    /** Starts a named timer. */
    time(name: string): void {
        this.sessions.set(name, { start: performance.now() });
    }

    /** Marks a timer as finished but preserves the data. */
    timeEnd(name: string, log: boolean = false): number | null {
        const session = this.sessions.get(name);
        if (!session) return null;

        session.end = performance.now();
        const duration = session.end - session.start;
        if (log) {
            console.log(`[track] ${name} ${duration.toFixed(2)}ms`);
        }
        return duration;
    }

    /** 
     * Returns the current duration of a timer without stopping it.
     * If already stopped, returns the final duration.
     */
    peek(name: string): number | null {
        const session = this.sessions.get(name);
        if (!session) return null;

        const endTime = session.end ?? performance.now();
        return endTime - session.start;
    }

    /** Returns all data (running and stopped) as a JSON string. */
    toJson(): string {
        const exportData: Record<string, { durationMs: number; status: string; }> = {};
        this.sessions.forEach((_, name) => {
            const duration = this.peek(name)!; // session is guaranteed to exist here
            const isRunning = !this.sessions.get(name)?.end;
            exportData[name] = {
                durationMs: Number(duration.toFixed(3)),
                status: isRunning ? "running" : "stopped"
            };
        });
        return JSON.stringify(exportData);
    }
}

export const TRACK_APP_START = 'App Start';
export const TRACK_SPLASH_SCREEN = 'Splash Screen';

export const tracker = new Tracker(); // global tracker instance for app performance and debugging
